/**
 * Server state for the management screens. The backend is the only source of truth: what is on screen is the
 * last read, and after every change the data is read again. Realtime events are refetch signals and never
 * carry state.
 *
 * Recovery, in order: refetch on `stream.ready` (every connect and reconnect), on `stream.resync`, when the
 * tab becomes visible again, and after every change. While the stream is not live a slow poll keeps the
 * screen honest; while it is live there is no polling at all. Nothing is retried by the client.
 */
import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useStaffSession } from '@/auth/react';
import { useToast } from '@/components/Toast';
import { useRealtimeEvent, useRealtimeState } from '@/realtime/react';
import { manageApi } from './api';

/** Poll interval while the live stream is down. Not used while it is live. */
export const FALLBACK_POLL_MS = 30_000;
/** Events that arrive together become one refetch. */
export const COALESCE_MS = 100;

type Family = 'floor' | 'help' | 'menu';
const ALL: Family[] = ['floor', 'help', 'menu'];

export const manageKeys = {
  family: (branchId: string | null, family: Family) => ['manage', branchId, family] as const,
};

function useBranchId(): string | null {
  return useStaffSession()?.user.branchId ?? null;
}

function useLiveRefetch() {
  return useRealtimeState() === 'live' ? false : FALLBACK_POLL_MS;
}

export function useLocations() {
  const branchId = useBranchId();
  const refetchInterval = useLiveRefetch();
  return useQuery({
    queryKey: manageKeys.family(branchId, 'floor'),
    queryFn: ({ signal }) => manageApi.getFloor(signal),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchInterval,
  });
}

/** Open help requests. `enabled` lets a screen read them only while the floor says someone asked for help. */
export function useHelpRequests(enabled = true) {
  const branchId = useBranchId();
  const refetchInterval = useLiveRefetch();
  return useQuery({
    queryKey: manageKeys.family(branchId, 'help'),
    queryFn: ({ signal }) => manageApi.getHelpRequests(signal),
    enabled,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchInterval,
  });
}

export function useManageMenu() {
  const branchId = useBranchId();
  const refetchInterval = useLiveRefetch();
  return useQuery({
    queryKey: manageKeys.family(branchId, 'menu'),
    queryFn: ({ signal }) => manageApi.getMenu(signal),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchInterval,
  });
}

/**
 * Maps staff stream events to refetches while a management screen is open. Duplicate and bursty events are
 * harmless: they only mark queries for refetch, and marks made within `COALESCE_MS` run once. Only events a
 * management screen depends on are subscribed. `bill.updated` and `payment.updated` carry amounts and nothing
 * here shows them; `cart.updated` and `order.*` change nothing on these screens.
 */
export function useManageLive(enabled = true) {
  const queryClient = useQueryClient();
  const branchId = useBranchId();
  const pending = useRef<Set<Family>>(new Set());
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(
    () => () => {
      if (timer.current !== undefined) clearTimeout(timer.current);
      timer.current = undefined;
    },
    [],
  );

  const mark = (...families: Family[]) => {
    if (!enabled) return;
    for (const family of families) pending.current.add(family);
    if (timer.current !== undefined) return;
    timer.current = setTimeout(() => {
      timer.current = undefined;
      const due = [...pending.current];
      pending.current = new Set();
      for (const family of due) {
        void queryClient.invalidateQueries({ queryKey: manageKeys.family(branchId, family) });
      }
    }, COALESCE_MS);
  };

  const floorChanged = () => mark('floor');
  const helpChanged = () => mark('floor', 'help');
  const everything = () => mark(...ALL);

  useRealtimeEvent('session.opened', floorChanged);
  useRealtimeEvent('session.transferred', helpChanged);
  useRealtimeEvent('session.closed', helpChanged);
  useRealtimeEvent('assistance.requested', helpChanged);
  useRealtimeEvent('assistance.resolved', helpChanged);
  useRealtimeEvent('menu.availability_changed', () => mark('menu'));
  useRealtimeEvent('stream.resync', everything);
  useRealtimeEvent('stream.ready', everything);
}

/**
 * Makes an item orderable or not. One call per press, never retried. After an answer (success or refusal)
 * the menu is read again, and a success stays pending until that read is done, so the button never shows a
 * state the server has not confirmed.
 */
export function useSetAvailability() {
  const queryClient = useQueryClient();
  const branchId = useBranchId();
  const toast = useToast();
  return useMutation({
    mutationFn: (vars: { itemId: string; itemName: string; isAvailable: boolean }) =>
      manageApi.setAvailability(vars.itemId, vars.isAvailable),
    onSuccess: (_result, vars) =>
      toast.show(
        vars.isAvailable
          ? `${vars.itemName} is available again.`
          : `${vars.itemName} is unavailable.`,
        { tone: 'success', durationMs: 4000 },
      ),
    onSettled: (_data, error) => {
      const read = queryClient.invalidateQueries({ queryKey: manageKeys.family(branchId, 'menu') });
      return error === null ? read : undefined;
    },
  });
}
